import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLightbox } from "@/components/LogoLightbox";

const IHM_LOGO_HD =
  "https://customer-assets.emergentagent.com/job_house-ihm-launch/artifacts/w2dm5l3i_5731B310-CF68-44D4-A984-BA5F338126ED.png";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/ventures", label: "Ventures" },
  { to: "/directors", label: "Directors" },
  { to: "/vision", label: "Vision" },
  { to: "/contact", label: "Contact" },
];

const Wordmark = () => {
  const { open } = useLightbox();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="navbar-logo"
        onClick={() => open(IHM_LOGO_HD, "House of IHM logo")}
        aria-label="View House of IHM logo fullscreen"
        className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-[#0E0E10] overflow-hidden cursor-zoom-in transition-transform duration-500 hover:rotate-[-6deg] hover:scale-[1.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2997FF] focus-visible:ring-offset-2"
      >
        <img
          src="/logo.png"
          alt="House of IHM logo"
          className="h-full w-full object-cover"
          loading="eager"
          decoding="async"
        />
      </button>
      <Link
        to="/"
        data-testid="navbar-home-link"
        aria-label="House of IHM EdTech home"
        className="hidden sm:flex flex-col leading-none"
      >
        <span className="font-heading font-bold text-[15px] tracking-tight text-[#1D1D1F]">
          House of IHM
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#86868B] mt-1 whitespace-nowrap">
          Where Vision Meets Execution
        </span>
      </Link>
    </div>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 w-full border-b border-[#E5E5EA]/60 bg-white/75 backdrop-blur-xl"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 h-16 md:h-[72px]">
        <Wordmark />

        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[14px] font-medium tracking-tight transition-colors duration-300 rounded-full ${
                    isActive
                      ? "text-[#1D1D1F]"
                      : "text-[#6E6E73] hover:text-[#1D1D1F]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative z-10 flex items-center gap-2">
                    {l.label}
                    {isActive && (
                      <span className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 h-[3px] w-[3px] rounded-full bg-[#2997FF]" />
                    )}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#1D1D1F] px-5 py-2.5 text-[13px] font-medium text-white transition-all duration-300 hover:bg-[#2997FF] hover:scale-[1.02]"
          >
            Get in touch
            <span aria-hidden>→</span>
          </Link>
        </div>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E5E5EA] text-[#1D1D1F] transition-colors hover:bg-[#F5F5F7]"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {/* Mobile sheet */}
      <div
        data-testid="nav-mobile-panel"
        className={`lg:hidden overflow-hidden border-t border-[#E5E5EA]/60 bg-white/95 backdrop-blur-xl transition-[max-height] duration-500 ease-in-out ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <ul className="flex flex-col px-6 py-4">
          {navLinks.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center justify-between py-4 border-b border-[#E5E5EA] last:border-0 text-[18px] font-heading tracking-tight transition-colors ${
                    isActive ? "text-[#1D1D1F]" : "text-[#6E6E73]"
                  }`
                }
              >
                {l.label}
                <span aria-hidden className="text-[#86868B]">↗</span>
              </NavLink>
            </li>
          ))}
          <li className="pt-4 pb-2">
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="nav-cta-contact-mobile"
              className="flex items-center justify-center w-full rounded-full bg-[#1D1D1F] px-5 py-3 text-[14px] font-medium text-white"
            >
              Get in touch →
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}
